<script lang="ts">
	import { createEventDispatcher, onMount } from 'svelte';
	import { field } from 'svelte-forms';
	import { min, required } from 'svelte-forms/validators';
	import { getTranslate } from '@tolgee/svelte';

	import InputWithLabel from '$lib/components/Form/Input/InputWithLabel.svelte';
	import { customForm } from '$lib/types/form';
	import Button from '../Button/Button.svelte';
	import Checkbox from './Input/Checkbox.svelte';
	import { signUpWithEmail } from '$lib/service/supabase/user.service';

	const { t } = getTranslate();
	const dispatch = createEventDispatcher();
	const minimumRequiredPasswordLegnth = 6;

	let loading: boolean = false;
	let before_signup_path: string = '';

	// TODO: Maybe enable constant validity checking after touch of formfield on username and email.
	const username = field('username', '', [required()]);
	const email = field('email', '', [required()]);
	const password = field('password', '', [required(), min(minimumRequiredPasswordLegnth)]);
	const receive_ebook = field('receive_ebook', false, []);
	const newsletter_accepted = field('newsletter_accepted', false, []);
	const signUpForm = customForm(username, email, password, receive_ebook, newsletter_accepted);

	// So that the message is visible
	$: if ($signUpForm.errors) refreshPasswordErrorMessage();

	$: emailErrorMessage = $t('form_emailRequiredErrorMessage');
	let passwordErrorMessage: string = '';
	let serverError: string = '';

	// TODO: Rethink this function to be suited for other cases too :) when there is time
	// TODO: Rework the determination - the async nature of the form validation makes
	// ----  it necessary to await the validation before checking for the errors.
	function refreshPasswordErrorMessage() {
		if ($signUpForm.hasError('password.required')) {
			passwordErrorMessage = $t('form_passwordRequiredErrorMessage');
		} else if ($signUpForm.hasError('password.min')) {
			passwordErrorMessage = $t('form_passwordMinErrorMessage');
		}
	}

	const handleSubmit = async () => {
		loading = true;
		await signUpForm.internalForm.validate();
		refreshPasswordErrorMessage();

		try {
			if (!$signUpForm.valid) {
				throw 'sign up form invalid';
			}

			const { user } = await signUpWithEmail(
				$email.value,
				$password.value,
				$username.value,
				$receive_ebook.value,
				$newsletter_accepted.value,
				before_signup_path
			);

			dispatch('success', user);
		} catch (error: any) {
			console.error('ERROR while signing up', { error });
			const { message } = error;
			serverError = message;
			dispatch('error', error);
		} finally {
			loading = false;
		}
	};

	onMount(() => {
		before_signup_path = localStorage.getItem('dsd_signUp_course') ?? '';
	});
</script>

<form class="space-y-4 lg:space-y-6" on:submit|preventDefault={handleSubmit}>
	<InputWithLabel
		type="text"
		id="username"
		placeholder={$t('signUpForm.username.placeholder')}
		bind:value={$username.value}
		errorClass="border-error"
		errorMessage={$t('form_usernameRequiredErrorMessage')}
		hasError={$signUpForm.hasError('username.required')}
		data-test="__signup-username"
	/>
	<InputWithLabel
		type="email"
		id="email"
		placeholder={$t('signUpForm.email.placeholder')}
		bind:value={$email.value}
		errorClass="border-error"
		errorMessage={emailErrorMessage}
		hasError={$signUpForm.hasError('email.required') ||
			($signUpForm.hasError('email.match_field') && !$signUpForm.hasError('email.required'))}
		data-test="__signup-email"
	/>
	<InputWithLabel
		type="password"
		id="password"
		placeholder={$t('signUpForm.password.placeholder')}
		bind:value={$password.value}
		errorClass="border-error"
		errorMessage={passwordErrorMessage}
		hasError={$signUpForm.hasError('password.required') ||
			($signUpForm.hasError('password.match_field') &&
				!$signUpForm.hasError('password.required')) ||
			$signUpForm.hasError('password.min')}
		data-test="__signup-password"
	/>
	<div class="flex flex-col gap-2">
		<Checkbox
			id="ebook"
			bind:checked={$receive_ebook.value}
			ariaLabel="ebook-checkbox"
			additionalClass={{ label: 'text-gray-500 dark:text-gray-300' }}
		>
			{@html $t('receive_ebook')}
		</Checkbox>
		<Checkbox
			id="newsletter"
			bind:checked={$newsletter_accepted.value}
			ariaLabel="newsletter-checkbox"
			additionalClass={{ label: 'text-gray-500 dark:text-gray-300' }}
		>
			{@html $t('signup_newsletter')}
		</Checkbox>
	</div>

	{#if serverError}
		<div class="text-error">{serverError}</div>
	{/if}

	<div class="text-center">
		<Button additionalClass="mt-4" type="submit" data-test="__signup-submit"
			>{@html $t('signup.form1.submit_button')}</Button
		>
		<p class="text-sm font-light mt-4">
			{@html $t('signup.form1.already_registered_01')}
			<a href="/auth/login" class="font-medium hover:underline text-purple"
				>{@html $t('signup.form1.already_registered_02')}</a
			>
		</p>
	</div>
</form>
